import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  ExecutionsHistoryOverviewSection,
  ExecutionsHistoryQuery,
  ExecutionsHistorySectionResult,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';
import ExecutionsHistoryDashboardService, {
  SECTIONS_WITH_SERIES,
} from '@src/services/ExecutionsHistoryDashboardService';

export const OVERVIEW_SECTIONS: ExecutionsHistoryOverviewSection[] = [
  'task-summary',
  'outcomes',
  'incidents-times',
  'templates',
  'users',
];

export interface HistorySectionState {
  loading: boolean;
  result?: ExecutionsHistorySectionResult;
}

type SectionsState = Record<ExecutionsHistoryOverviewSection, HistorySectionState>;

interface CachedResult {
  fetchedAt: number;
  result: ExecutionsHistorySectionResult;
}

/** Successful results are reused for this long before the section is requested again. */
export const SECTION_REUSE_MS = 30_000;

const buildSectionsState = (state: HistorySectionState): SectionsState =>
  Object.fromEntries(OVERVIEW_SECTIONS.map(section => [section, state])) as SectionsState;

/** Filters that affect a section: the interval only matters to sections with series. */
const getSectionKey = (
  section: ExecutionsHistoryOverviewSection,
  { interval, ...filters }: ExecutionsHistoryQuery,
): string => JSON.stringify(SECTIONS_WITH_SERIES.has(section) ? { ...filters, interval } : filters);

/**
 * Loads every overview section of the Tasks report in parallel, each with its own state.
 * Changing filters only requests the sections they affect; `refresh` requests them all.
 */
/** `enabled: false` holds every request, e.g. while the report divisions are still unknown. */
export const useExecutionsHistorySections = (
  query: ExecutionsHistoryQuery,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const serviceRef = useRef<ExecutionsHistoryDashboardService | null>(null);
  if (!serviceRef.current) serviceRef.current = new ExecutionsHistoryDashboardService();
  const requestedKeysRef = useRef<Partial<Record<ExecutionsHistoryOverviewSection, string>>>({});
  const generationsRef = useRef<Partial<Record<ExecutionsHistoryOverviewSection, number>>>({});
  const cacheRef = useRef(new Map<string, CachedResult>());
  const queryRef = useRef(query);
  queryRef.current = query;
  const [sections, setSections] = useState<SectionsState>(() => buildSectionsState({ loading: true }));

  const loadSection = useCallback(
    async (section: ExecutionsHistoryOverviewSection, { force }: { force: boolean }) => {
      const currentQuery = queryRef.current;
      const sectionKey = getSectionKey(section, currentQuery);
      if (!force && requestedKeysRef.current[section] === sectionKey) return;
      requestedKeysRef.current[section] = sectionKey;
      // Any response still in flight for this section is now stale.
      const generation = (generationsRef.current[section] ?? 0) + 1;
      generationsRef.current[section] = generation;

      const cacheKey = `${section}|${sectionKey}`;
      const cached = cacheRef.current.get(cacheKey);
      if (!force && cached && Date.now() - cached.fetchedAt < SECTION_REUSE_MS) {
        setSections(current => ({ ...current, [section]: { loading: false, result: cached.result } }));
        return;
      }

      setSections(current => ({ ...current, [section]: { ...current[section], loading: true } }));
      const result = await (serviceRef.current as ExecutionsHistoryDashboardService).getSection(
        section,
        currentQuery,
      );
      if (result.status === 'ok') cacheRef.current.set(cacheKey, { fetchedAt: Date.now(), result });
      if (generationsRef.current[section] !== generation) return;
      setSections(current => ({ ...current, [section]: { loading: false, result } }));
    },
    [],
  );

  const queryKey = JSON.stringify(query);

  useEffect(() => {
    if (!enabled) return;
    OVERVIEW_SECTIONS.forEach(section => loadSection(section, { force: false }));
  }, [queryKey, loadSection, enabled]);

  const refresh = useCallback(() => {
    if (!enabled) return;
    OVERVIEW_SECTIONS.forEach(section => loadSection(section, { force: true }));
  }, [loadSection, enabled]);

  const retry = useCallback(
    (section: ExecutionsHistoryOverviewSection) => {
      if (enabled) loadSection(section, { force: true });
    },
    [loadSection, enabled],
  );

  /** True while any section is waiting for its answer. */
  const loading = OVERVIEW_SECTIONS.some(section => sections[section].loading);

  return { sections, loading, refresh, retry };
};
